import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Subject, BehaviorSubject, Observable, of, merge } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  map,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BackendApiService } from '../../../../services/backend-api.service';
import { UserService } from '../../../../services/core/user.service';
import { StaffService } from '../../../../services/staff/staff.service';
import { ticketAnimations } from '../ticket-animations';
import { TicketDataService } from '../../ticket.data.service';
import { JobDataService } from '../../../sale/job/job.data.service';
import { CustomerDataService } from '../../../customer/customer.data.service';
import { VendorDataService } from '../../../inventory/vendor/vendor.data.service';
import { SubcontractorDataService } from '../../../inventory/subcontractor/subcontractor.data.service';
import { SettingDataService } from '../../../setting/setting.data.service';
import { TicketUtilsService } from '../../ticket-utils.service';
import { TICKET_MODULE_ID } from '../../ticket.constants';

/**
 * Interface for sidebar field change events
 */
export interface TicketSidebarFieldChange {
  field: string;
  value: any;
  validation?: any;
}

/**
 * Interface for user/person objects with type support
 */
export interface TicketPerson {
  id?: number;
  name?: string;
  full_name?: string;
  company_name?: string; // For customers
  email?: string;
  avatar?: string;
  profile_url?: string;
  profile_image?: string;
  type: 'Users' | 'Vendors' | 'Customers' | 'Subcontractors';
}

/**
 * Interface for milestone objects
 */
export interface TicketMilestone {
  id: number;
  name: string;
  dueDate: string | Date;
  status: 'upcoming' | 'in-progress' | 'completed' | 'overdue';
  startDate?: string | Date;
  endDate?: string | Date;
  progress?: number;
  taskStats?: {
    total: number;
    completed: number;
  };
  daysRemaining?: number;
  description?: string;
  assignedTo?: string;
}

/**
 * Interface for tag objects
 */
export interface TicketTag {
  id?: number;
  name: string;
  tag?: string;
  color: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * TicketSidebarComponent
 *
 * Dark-themed sidebar panel for ticket details and metadata.
 * Features collapsible layout, milestone selector, people management with type support, and tags.
 * Adapted from task-sidebar to support ticket-specific fields.
 *
 * @selector app-ticket-sidebar
 * @changeDetection OnPush
 */
@Component({
    selector: 'app-ticket-sidebar',
    templateUrl: './ticket-sidebar.component.html',
    styleUrls: ['./ticket-sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: ticketAnimations,
    standalone: false
})
export class TicketSidebarComponent implements OnInit, OnDestroy, OnChanges {
  private readonly ticketModuleId = TICKET_MODULE_ID;
  private readonly selfAssignModuleId = 11;
  // =============================================================================
  // INPUTS
  // =============================================================================

  @Input() ticketForm!: FormGroup;
  @Input() ticketId: string | number = '';
  /**
   * Ticket display code (e.g. TIC-328-471-CUST).
   * When provided, this will be shown in the sidebar header instead of the numeric ID.
   */
  @Input() ticketCode: string | null = null;
  @Input() mode: 'create' | 'edit' | 'view' = 'create';

  // Milestone & Tags
  @Input() milestones: TicketMilestone[] = [];
  @Input() selectedMilestone: TicketMilestone | null = null;
  @Input() tags: TicketTag[] = [];
  @Input() systemTags: TicketTag[] = [];

  // People
  @Input() assignees: TicketPerson[] = [];
  @Input() observers: TicketPerson[] = [];
  @Input() participants: TicketPerson[] = [];
  /** The logged-in user's saved default person per field (GET
   * ticket/default-people, fetched once by the parent, create mode only), keyed by
   * 'assignee'/'observer'/'participant'. */
  @Input() savedDefaults: any = {};

  // Ticket-specific fields
  @Input() ticketTypes: any[] = [];
  @Input() scopes: any[] = [];
  @Input() customers: any[] = [];
  @Input() contacts: any[] = [];
  @Input() vendors: any[] = [];
  @Input() subcontractors: any[] = [];

  // Selected entities for display (matches task pattern)
  @Input() selectedCustomer: any = null;
  @Input() selectedContact: any = null;
  @Input() selectedVendor: any = null;
  @Input() selectedSubcontractor: any = null;
  @Input() selectedResponsibleParty: any = null;

  // Status & Priority
  @Input() statuses: any[] = [];
  @Input() selectedStatus: any = null;
  @Input() priorities: any[] = [
    { id: 1, name: 'Low', color: '#3b82f6' },
    { id: 2, name: 'Medium', color: '#eab308' },
    { id: 3, name: 'High', color: '#ef4444' },
    { id: 4, name: 'Urgent', color: '#9333ea' },
  ];

  // Module & Reference
  @Input() modules: any[] = [];
  @Input() showModuleControls: boolean = true;

  /**
   * Ticket details stay locked until the ticket has a context.
   *
   * Scopes, Customer, Vendor and the rest are all resolved RELATIVE to the
   * chosen module and record — the scope list, for instance, is the job's scopes
   * once a job is picked and the office catalogue otherwise. Choosing them first
   * would mean picking from a list that is about to be replaced.
   *
   * Only the standalone create flow is gated: opening from a Job (or any other
   * record) already has both, and edit/view never ask for them.
   */
  get isContextReady(): boolean {
    if (this.mode !== 'create' || !this.showModuleControls) {
      return true;
    }

    // A self-assigned ticket has no module/reference to choose — the fields are
    // hidden entirely, so there is nothing to wait for.
    if (Number(this.ticketForm?.get('self_assign')?.value) === 1) {
      return true;
    }

    return !!this.ticketForm?.get('module_id')?.value
        && !!this.ticketForm?.get('reference_id')?.value;
  }

  // Metadata
  @Input() completionPercentage: number = 0;
  @Input() checklist: any[] = [];
  @Input() createdOn: Date = new Date();

  // UI State
  @Input() isCollapsed: boolean = false;
  @Input() isViewMode: boolean = false;
  @Input() isReadOnly: boolean = false;
  @Input() isLocked: boolean = false;

  // =============================================================================
  // OUTPUTS
  // =============================================================================

  @Output() onCollapse = new EventEmitter<boolean>();
  @Output() onSearch = new EventEmitter<string>();
  @Output() fieldChange = new EventEmitter<TicketSidebarFieldChange>();

  // Status & Priority
  @Output() statusChange = new EventEmitter<any>();
  @Output() priorityChange = new EventEmitter<number>();

  // Milestone
  @Output() milestoneSelected = new EventEmitter<TicketMilestone>();
  @Output() milestoneRemoved = new EventEmitter<void>();

  // People
  @Output() assigneeSelected = new EventEmitter<TicketPerson>();
  @Output() assigneeRemoved = new EventEmitter<number>();
  @Output() observerSelected = new EventEmitter<TicketPerson>();
  @Output() observerRemoved = new EventEmitter<number>();
  @Output() participantSelected = new EventEmitter<TicketPerson>();
  @Output() participantRemoved = new EventEmitter<number>();
  // Emits when the user clicks a different row's star in a picker dropdown
  // to make that one the default instead of whichever was positionally first.
  @Output() defaultAssigneeChanged = new EventEmitter<any>();
  @Output() defaultObserverChanged = new EventEmitter<any>();
  @Output() defaultParticipantChanged = new EventEmitter<any>();

  // Tags
  @Output() tagSelectorOpened = new EventEmitter<void>();
  @Output() tagRemoved = new EventEmitter<number>();
  @Output() tagsUpdated = new EventEmitter<TicketTag[]>();

  // Time Planning
  @Output() timePlanningToggle = new EventEmitter<boolean>();
  @Output() startDateChange = new EventEmitter<Date>();
  @Output() durationChange = new EventEmitter<number>();
  @Output() finishDateChange = new EventEmitter<Date>();

  // Module & Reference
  @Output() moduleSelected = new EventEmitter<any>();
  @Output() referenceSelected = new EventEmitter<any>();
  @Output() selfAssignChange = new EventEmitter<boolean>();

  // Ticket-specific
  @Output() ticketTypeSelected = new EventEmitter<any>();
  @Output() scopesChanged = new EventEmitter<any[]>();
  @Output() customerSelected = new EventEmitter<any>();
  @Output() contactSelected = new EventEmitter<any>();
  @Output() vendorSelected = new EventEmitter<any>();
  @Output() subcontractorSelected = new EventEmitter<any>();
  @Output() responsiblePartySelected = new EventEmitter<any>();

  // =============================================================================
  // VIEW CHILDREN
  // =============================================================================

  @ViewChild('milestonePopup', { read: ElementRef })
  milestonePopupRef?: ElementRef;
  @ViewChild('tagsNgSelect') tagsNgSelect?: any;

  @ViewChild('assigneeSelect') assigneeSelectRef?: NgSelectComponent;
  @ViewChild('observerSelect') observerSelectRef?: NgSelectComponent;
  @ViewChild('participantSelect') participantSelectRef?: NgSelectComponent;

  // =============================================================================
  // PUBLIC PROPERTIES
  // =============================================================================

  public searchQuery: string = '';
  public showMilestoneSelector: boolean = false;
  public isToggleButtonHovered: boolean = false;
  public isTimePlanningExpanded: boolean = false;
  public timePlanningError: string = '';

  // Inline search visibility
  public showAssigneeSearch: boolean = false;
  public showObserverSearch: boolean = false;
  public showParticipantSearch: boolean = false;

  // User search streams
  public assigneeSearchInput$ = new Subject<string>();
  public observerSearchInput$ = new Subject<string>();
  public participantSearchInput$ = new Subject<string>();

  /**
   * Paginated people-picker state. Each list starts as a browsable default
   * page (empty search term, current active person type) as soon as its
   * search field opens or its type filter changes, and appends further pages
   * via ng-select's (scrollToEnd); typing a search term restarts pagination
   * from page 1. Only Users/Vendors have confirmed backend pagination
   * support — Customers/Subcontractors return a flat, unpaginated list.
   */
  private static readonly PEOPLE_PER_PAGE = 12;
  private static readonly SKELETON_ROW_COUNT = 4;
  private readonly PAGINATED_PERSON_TYPES = ['Users', 'Vendors'];

  public assigneeItems: any[] = [];
  public observerItems: any[] = [];
  public participantItems: any[] = [];

  private assigneeSearchTerm = '';
  private observerSearchTerm = '';
  private participantSearchTerm = '';

  private assigneeCurrentPage = 1;
  private observerCurrentPage = 1;
  private participantCurrentPage = 1;

  private assigneeHasMore = true;
  private observerHasMore = true;
  private participantHasMore = true;

  public assigneeLoadingMore = false;
  public observerLoadingMore = false;
  public participantLoadingMore = false;

  // Reference search
  public referenceSearchInput$ = new Subject<string>();
  public referenceSearchResults$: Observable<any[]> = of([]);
  public referenceSearchLoading: boolean = false;

  // Loading states
  public assigneeSearchLoading: boolean = false;
  public observerSearchLoading: boolean = false;
  public participantSearchLoading: boolean = false;

  // Customer/Vendor/Subcontractor search streams
  public customerSearchInput$ = new Subject<string>();
  public vendorSearchInput$ = new Subject<string>();
  public subcontractorSearchInput$ = new Subject<string>();

  /**
   * Options pushed in from outside the typeahead — the edit-mode preselect and
   * the job preload. Merged into the results stream rather than replacing it:
   * assigning `vendorSearchResults$ = of([...])` throws the typeahead pipeline
   * away, which is why searching stopped working after a value was set.
   */
  /** The module:record pair the current options were loaded for. */
  private loadedPartyKey: string | null = null;

  private vendorOptions$ = new BehaviorSubject<any[]>([]);
  private subcontractorOptions$ = new BehaviorSubject<any[]>([]);

  public customerSearchResults$: Observable<any[]> = of([]);
  public vendorSearchResults$: Observable<any[]> = of([]);
  public subcontractorSearchResults$: Observable<any[]> = of([]);

  public customerSearchLoading: boolean = false;
  public vendorSearchLoading: boolean = false;
  public subcontractorSearchLoading: boolean = false;

  // Responsible parties
  public responsibleParties: any[] = [];

  // People type filter
  public selectedPersonType:
    | 'Users'
    | 'Vendors'
    | 'Customers'
    | 'Subcontractors' = 'Users';

  // Active person types for each role
  public activeAssigneePersonType:
    | 'Users'
    | 'Vendors'
    | 'Customers'
    | 'Subcontractors' = 'Users';
  public activeObserverPersonType:
    | 'Users'
    | 'Vendors'
    | 'Customers'
    | 'Subcontractors' = 'Users';
  public activeParticipantPersonType:
    | 'Users'
    | 'Vendors'
    | 'Customers'
    | 'Subcontractors' = 'Users';

  // Person types configuration
  public personTypes: Array<
    'Users' | 'Vendors' | 'Customers' | 'Subcontractors'
  > = ['Users', 'Vendors', 'Customers', 'Subcontractors'];

  // Cleanup subject
  private destroy$ = new Subject<void>();

  // =============================================================================
  // TAG PROPERTIES
  // =============================================================================

  /**
   * Tag color mappings
   */
  private readonly TAG_COLORS: { [key: string]: { bg: string; text: string } } =
    {
      blue: { bg: 'rgba(59, 130, 246, 0.2)', text: '#93c5fd' },
      green: { bg: 'rgba(34, 197, 94, 0.2)', text: '#86efac' },
      yellow: { bg: 'rgba(234, 179, 8, 0.2)', text: '#fde047' },
      red: { bg: 'rgba(239, 68, 68, 0.2)', text: '#fca5a5' },
      purple: { bg: 'rgba(168, 85, 247, 0.2)', text: '#d8b4fe' },
      orange: { bg: 'rgba(249, 115, 22, 0.2)', text: '#fdba74' },
      gray: { bg: 'rgba(107, 114, 128, 0.2)', text: '#d1d5db' },
    };

  /**
   * Unified tags dropdown state
   */
  public isTagsDropdownOpen: boolean = false;
  public selectedTagsForDropdown: TicketTag[] = [];
  public tagSearchQuery: string = '';

  /**
   * Get displayed tags - shows tags input (current state)
   */
  public get displayedTags(): TicketTag[] {
    return this.tags || [];
  }

  /**
   * Get combined tags for dropdown (system tags + ticket tags merged by name)
   */
  public get combinedTagsForDropdown(): TicketTag[] {
    const ticketTags = this.tags || [];
    const apiTags = this.systemTags || [];

    // Create a map to merge by name (case-insensitive)
    const tagMap = new Map<string, TicketTag>();

    // Add system tags first
    apiTags.forEach((tag) => {
      const key = (tag.name || tag.tag || '').toLowerCase();
      if (!tagMap.has(key)) {
        tagMap.set(key, tag);
      }
    });

    // Add ticket tags (if not already in map from system tags)
    ticketTags.forEach((tag) => {
      const key = (tag.name || tag.tag || '').toLowerCase();
      if (!tagMap.has(key)) {
        tagMap.set(key, tag);
      }
    });

    return Array.from(tagMap.values());
  }

  /**
   * Filtered tags based on search query
   */
  public get filteredStaticTags(): TicketTag[] {
    if (!this.tagSearchQuery.trim()) {
      return this.combinedTagsForDropdown;
    }
    const query = this.tagSearchQuery.toLowerCase().trim();
    return this.combinedTagsForDropdown.filter((tag) =>
      (tag.name || tag.tag || '').toLowerCase().includes(query)
    );
  }

  /**
   * Check if a tag is selected in dropdown
   */
  public isTagSelectedInDropdown(tag: TicketTag): boolean {
    return this.selectedTagsForDropdown.some(
      (selected) =>
        (selected.name || selected.tag || '').toLowerCase() ===
        (tag.name || tag.tag || '').toLowerCase()
    );
  }

  // =============================================================================
  // CONSTRUCTOR & LIFECYCLE
  // =============================================================================

  constructor(
    private api: BackendApiService,
    private userService: UserService,
    private staffService: StaffService,
    private cdr: ChangeDetectorRef,
    private ticketDataService: TicketDataService,
    private jobDataService: JobDataService,
    private customerDataService: CustomerDataService,
    private vendorDataService: VendorDataService,
    private subcontractorDataService: SubcontractorDataService,
    private settingDataService: SettingDataService,
    private ticketUtilsService: TicketUtilsService
  ) {}

  ngOnInit(): void {
    this.initializeSearchStreams();
    this.initializeTimePlanning();
    this.fetchSystemTags();
    this.initializeContextualPreselectionWatcher();
    this.initializeEditModeEntityWatcher();
    this.initializeContextPartyWatcher();
    this.loadResponsibleParties();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ticketForm'] && this.ticketForm) {
      this.syncFormToState();
    }

    // The module list arrives async. Once it does, re-resolve the party options
    // so the slug — not the id fallback — decides which endpoint was right.
    if (changes['modules'] && !changes['modules'].firstChange) {
      this.loadedPartyKey = null;
      this.preloadContextParties();
    }

    // Watch ticketTypes input changes for OnPush detection
    if (changes['ticketTypes']) {
      console.log(
        'Sidebar: ticketTypes changed to',
        changes['ticketTypes'].currentValue
      );
      this.cdr.markForCheck();
    }

    // Watch scopes input changes for OnPush detection
    if (changes['scopes']) {
      console.log('Sidebar: scopes changed to', changes['scopes'].currentValue);
      this.cdr.markForCheck();
    }

    // Watch statuses input changes for OnPush detection
    if (changes['statuses']) {
      console.log(
        'Sidebar: statuses changed to',
        changes['statuses'].currentValue
      );
      this.cdr.markForCheck();
    }

    // Watch customers input changes for OnPush detection
    if (changes['customers']) {
      console.log(
        'Sidebar: customers changed to',
        changes['customers'].currentValue
      );
      this.cdr.markForCheck();
    }

    // Watch vendors input changes for OnPush detection
    if (changes['vendors']) {
      console.log(
        'Sidebar: vendors changed to',
        changes['vendors'].currentValue
      );
      this.cdr.markForCheck();
    }

    // Watch subcontractors input changes for OnPush detection
    if (changes['subcontractors']) {
      console.log(
        'Sidebar: subcontractors changed to',
        changes['subcontractors'].currentValue
      );
      this.cdr.markForCheck();
    }

    // Watch contacts input changes for OnPush detection
    if (changes['contacts']) {
      console.log(
        'Sidebar: contacts changed to',
        changes['contacts'].currentValue
      );
      this.cdr.markForCheck();
    }

    // Watch checklist input changes for OnPush detection
    if (changes['checklist']) {
      console.log(
        'Sidebar: checklist changed to',
        changes['checklist'].currentValue
      );
      this.cdr.markForCheck();
    }

    // Watch modules input changes for OnPush detection
    if (changes['modules']) {
      console.log(
        'Sidebar: modules changed to',
        changes['modules'].currentValue
      );
      this.cdr.markForCheck();
    }

    // Always sync selectedTagsForDropdown with tags input for all modes
    if (changes['tags']) {
      this.selectedTagsForDropdown = [...(this.tags || [])];
      // Trigger change detection to ensure UI updates
      this.cdr.markForCheck();
    }

    // Initialize selectedTagsForDropdown when ticketId changes
    if (changes['ticketId'] && this.tags) {
      this.selectedTagsForDropdown = [...(this.tags || [])];
      this.cdr.markForCheck();
    }
  }

  // =============================================================================
  // INITIALIZATION METHODS
  // =============================================================================

  private initializeSearchStreams(): void {
    // Assignee: typing restarts pagination from page 1 (empty term = default browse list)
    this.assigneeSearchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term: string) => {
          this.assigneeSearchTerm = term ?? '';
          this.assigneeSearchLoading = true;
          this.assigneeItems = this.buildPeopleSkeletonRows();
          this.cdr.markForCheck();
        }),
        switchMap((term: string) =>
          this.fetchPeoplePage(this.activeAssigneePersonType, term ?? '', 1, 'assignee')
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.assigneeItems = result.items;
        this.assigneeCurrentPage = result.currentPage;
        this.assigneeHasMore = result.hasMore;
        this.assigneeSearchLoading = false;
        this.cdr.markForCheck();
      });

    // Observer: typing restarts pagination from page 1 (empty term = default browse list)
    this.observerSearchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term: string) => {
          this.observerSearchTerm = term ?? '';
          this.observerSearchLoading = true;
          this.observerItems = this.buildPeopleSkeletonRows();
          this.cdr.markForCheck();
        }),
        switchMap((term: string) =>
          this.fetchPeoplePage(this.activeObserverPersonType, term ?? '', 1, 'observer')
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.observerItems = result.items;
        this.observerCurrentPage = result.currentPage;
        this.observerHasMore = result.hasMore;
        this.observerSearchLoading = false;
        this.cdr.markForCheck();
      });

    // Participant: typing restarts pagination from page 1 (empty term = default browse list)
    this.participantSearchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term: string) => {
          this.participantSearchTerm = term ?? '';
          this.participantSearchLoading = true;
          this.participantItems = this.buildPeopleSkeletonRows();
          this.cdr.markForCheck();
        }),
        switchMap((term: string) =>
          this.fetchPeoplePage(this.activeParticipantPersonType, term ?? '', 1, 'participant')
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.participantItems = result.items;
        this.participantCurrentPage = result.currentPage;
        this.participantHasMore = result.hasMore;
        this.participantSearchLoading = false;
        this.cdr.markForCheck();
      });

    // Reference search
    this.referenceSearchResults$ = this.referenceSearchInput$.pipe(
      tap(() => {
        // Set loading IMMEDIATELY when user starts typing
        this.referenceSearchLoading = true;
        this.cdr.markForCheck();
      }),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        const searchTerm = (term || '').trim();
        if (!searchTerm || searchTerm.length < 2) {
          // Reset loading if term is too short
          this.referenceSearchLoading = false;
          this.cdr.markForCheck();
          return of([]);
        }
        const moduleId = this.ticketForm.get('module_id')?.value;
        if (!moduleId) {
          this.referenceSearchLoading = false;
          this.cdr.markForCheck();
          return of([]);
        }
        return this.searchReferences(searchTerm, moduleId).pipe(
          map((results) => {
            this.referenceSearchLoading = false;
            this.cdr.markForCheck();
            return results;
          }),
          catchError(() => {
            this.referenceSearchLoading = false;
            this.cdr.markForCheck();
            return of([]);
          })
        );
      }),
      takeUntil(this.destroy$)
    );

    // Customer search - Context-aware
    this.customerSearchResults$ = this.customerSearchInput$.pipe(
      tap(() => {
        // Set loading IMMEDIATELY when user starts typing
        this.customerSearchLoading = true;
        this.cdr.markForCheck();
      }),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 2) {
          // Reset loading if term is too short
          this.customerSearchLoading = false;
          this.cdr.markForCheck();
          return of([]);
        }

        // Extract context from form
        const moduleId = this.ticketForm?.get('module_id')?.value;
        const referenceId = this.ticketForm?.get('reference_id')?.value;

        return this.searchCustomersContextual(term, moduleId, referenceId).pipe(
          map((results) => {
            this.customerSearchLoading = false;
            this.cdr.markForCheck();
            return results;
          }),
          catchError(() => {
            this.customerSearchLoading = false;
            this.cdr.markForCheck();
            return of([]);
          })
        );
      }),
      takeUntil(this.destroy$)
    );

    // Vendor search - Context-aware
    this.vendorSearchResults$ = merge(this.vendorOptions$, this.vendorSearchInput$.pipe(
      tap(() => {
        // Set loading IMMEDIATELY when user starts typing
        this.vendorSearchLoading = true;
        this.cdr.markForCheck();
      }),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 2) {
          // Reset loading if term is too short
          this.vendorSearchLoading = false;
          this.cdr.markForCheck();
          return of([]);
        }

        // Extract context from form
        const moduleId = this.ticketForm?.get('module_id')?.value;
        const referenceId = this.ticketForm?.get('reference_id')?.value;

        return this.searchVendorsContextual(term, moduleId, referenceId).pipe(
          map((results) => {
            this.vendorSearchLoading = false;
            this.cdr.markForCheck();
            return results;
          }),
          catchError(() => {
            this.vendorSearchLoading = false;
            this.cdr.markForCheck();
            return of([]);
          })
        );
      }),
      takeUntil(this.destroy$)
    ));

    // Subcontractor search - Context-aware
    this.subcontractorSearchResults$ = merge(this.subcontractorOptions$, this.subcontractorSearchInput$.pipe(
      tap(() => {
        // Set loading IMMEDIATELY when user starts typing
        this.subcontractorSearchLoading = true;
        this.cdr.markForCheck();
      }),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 2) {
          // Reset loading if term is too short
          this.subcontractorSearchLoading = false;
          this.cdr.markForCheck();
          return of([]);
        }

        // Extract context from form
        const moduleId = this.ticketForm?.get('module_id')?.value;
        const referenceId = this.ticketForm?.get('reference_id')?.value;

        return this.searchSubcontractorsContextual(
          term,
          moduleId,
          referenceId
        ).pipe(
          map((results) => {
            this.subcontractorSearchLoading = false;
            this.cdr.markForCheck();
            return results;
          }),
          catchError(() => {
            this.subcontractorSearchLoading = false;
            this.cdr.markForCheck();
            return of([]);
          })
        );
      }),
      takeUntil(this.destroy$)
    ));
  }

  /** modules.id for Job — the fallback while the module list is still loading. */
  private readonly JOB_MODULE_ID = 19;

  /**
   * True when the given module is Job.
   *
   * Prefers the slug, but `modules` is fetched asynchronously and in EDIT mode
   * the form is patched before it arrives. Resolving to "not Job" during that
   * window silently routed the dropdowns to the old endpoint — which filters to
   * material lines only, hence 5 vendors on job 100 instead of 8. Falling back
   * to the known id closes that gap.
   */
  private isJobModule(moduleId: any): boolean {
    const module = (this.modules || []).find((m: any) => m.id == moduleId);

    if (module) {
      return (module.slug || '').toLowerCase() === 'job';
    }

    return Number(moduleId) === this.JOB_MODULE_ID;
  }

  /**
   * Preload vendors and subcontractors as soon as a module AND record are chosen.
   *
   * Routed through the same *Contextual() methods the typeahead uses, with an
   * empty term — so each module automatically gets whichever endpoint is right
   * for it (scoped for Job, the original for everything else) without a second
   * copy of that decision.
   *
   * These lists are short enough that making the user type first is pointless.
   * With exactly ONE candidate the field is filled in automatically; anything
   * more is left for the dropdown.
   */
  private preloadContextParties(): void {
    const moduleId = this.ticketForm?.get('module_id')?.value;
    const referenceId = this.ticketForm?.get('reference_id')?.value;

    // Both are required: without a record there is nothing to narrow by, and the
    // generic fallback would return the entire vendor list.
    if (!moduleId || !referenceId) {
      this.loadedPartyKey = null;
      this.vendorOptions$.next([]);
      this.subcontractorOptions$.next([]);
      return;
    }

    // onReferenceSelect() and the form watcher can both land on the same pair;
    // load it once.
    const key = this.contextPartyKey();
    if (key === this.loadedPartyKey) {
      return;
    }
    this.loadedPartyKey = key;

    this.searchVendorsContextual('', moduleId, referenceId).subscribe({
      next: (rows: any[]) => this.applyPartyOptions(
        rows || [], this.vendorOptions$, 'vendor',
        (row) => this.onVendorChange(row),
      ),
      error: () => this.vendorOptions$.next([]),
    });

    this.searchSubcontractorsContextual('', moduleId, referenceId).subscribe({
      next: (rows: any[]) => this.applyPartyOptions(
        rows || [], this.subcontractorOptions$, 'subcontractor',
        (row) => this.onSubcontractorChange(row),
      ),
      error: () => this.subcontractorOptions$.next([]),
    });
  }

  /** Publish the options, auto-selecting when there is only one candidate. */
  private applyPartyOptions(
    rows: any[],
    options$: BehaviorSubject<any[]>,
    controlName: string,
    notify: (row: any) => void,
  ): void {
    options$.next(rows);

    // Never overwrite a value the user (or the loaded ticket) already set.
    const current = this.ticketForm?.get(controlName)?.value;
    if (rows.length === 1 && !current) {
      this.ticketForm?.get(controlName)?.setValue(rows[0].id);
      notify(rows[0]);
    }

    this.cdr.markForCheck();
  }

  private initializeTimePlanning(): void {
    const isTimePlanning = this.ticketForm?.get('is_time_planning')?.value;
    this.isTimePlanningExpanded = !!isTimePlanning;
  }

  private syncFormToState(): void {
    // When editing an existing ticket, populate search results observables
    // so ng-select can display the preselected entity names

    // Handle customer preloading for edit mode
    const customerId = this.ticketForm?.get('customer')?.value;
    if (customerId && typeof customerId === 'number') {
      this.api
        .getApi(`customers/${customerId}`)
        .pipe(
          map((res: any) => res?.data || null),
          catchError(() => of(null))
        )
        .subscribe((customer: any) => {
          if (customer) {
            this.customerSearchResults$ = of([customer]);

            // CRITICAL: Emit customer to parent so contacts array is populated
            // This triggers getCustomerContacts() which populates the contacts dropdown
            this.customerSelected.emit(customer);

            this.cdr.markForCheck();
          }
        });
    }

    // Handle vendor preloading for edit mode
    const vendorId = this.ticketForm?.get('vendor')?.value;
    if (vendorId && typeof vendorId === 'number') {
      this.api
        .getApi(`vendors/${vendorId}`)
        .pipe(
          map((res: any) => res?.data || null),
          catchError(() => of(null))
        )
        .subscribe((vendor: any) => {
          if (vendor) {
            this.vendorOptions$.next([vendor]);
            this.cdr.markForCheck();
          }
        });
    }

    // Handle subcontractor preloading for edit mode
    const subcontractorId = this.ticketForm?.get('subcontractor')?.value;
    if (subcontractorId && typeof subcontractorId === 'number') {
      this.api
        .getApi(`subcontractors/${subcontractorId}`)
        .pipe(
          map((res: any) => res?.data || null),
          catchError(() => of(null))
        )
        .subscribe((subcontractor: any) => {
          if (subcontractor) {
            this.subcontractorOptions$.next([subcontractor]);
            this.cdr.markForCheck();
          }
        });
    }
  }

  /**
   * Initialize watcher for contextual preselection
   * Subscribes to form value changes to detect when module_id and reference_id are set
   * This handles the timing issue where ngOnChanges fires before parent sets these values
   */
  private initializeContextualPreselectionWatcher(): void {
    // Wait for ticketForm to be available and subscribe to its value changes
    if (!this.ticketForm) {
      return;
    }

    this.ticketForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(100), // Small delay to ensure both values are set
        map(() => ({
          moduleId: this.ticketForm.get('module_id')?.value,
          referenceId: this.ticketForm.get('reference_id')?.value,
          customer: this.ticketForm.get('customer')?.value,
        })),
        distinctUntilChanged(
          (prev, curr) =>
            prev.moduleId === curr.moduleId &&
            prev.referenceId === curr.referenceId &&
            prev.customer === curr.customer
        )
      )
      .subscribe(({ moduleId, referenceId, customer }) => {
        // Only attempt preselection if context exists and customer is not already set
        // Skip API calls when self-assign is ON (module_id = 11)
        // CRITICAL FIX: Only run in edit mode when form is being populated from existing data
        // In create mode, user manually selecting module/reference should NOT trigger this
        if (
          moduleId &&
          referenceId &&
          !customer &&
          moduleId !== this.selfAssignModuleId &&
          this.mode === 'edit' // Only auto-preselect in edit mode, not during manual create
        ) {
          this.initializeContextualPreselection(moduleId, referenceId);
        }
      });
  }

  /**
   * Watch for entity ID changes in edit mode and preload for display
   * This handles the case where form values change after initial binding (e.g., in edit mode)
   * ngOnChanges only fires when input reference changes, not when values inside form change
   */
  /**
   * Keep the vendor/subcontractor options in step with the ticket's module and
   * record, however they get set.
   *
   * In CREATE the user picks them, so onReferenceSelect() could drive this. In
   * EDIT they arrive from the loaded ticket and no handler ever runs — which is
   * why the dropdowns came up empty there. Watching the form covers both.
   */
  private initializeContextPartyWatcher(): void {
    if (!this.ticketForm) {
      return;
    }

    this.ticketForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(150),
        map(() => this.contextPartyKey()),
        distinctUntilChanged()
      )
      .subscribe(() => this.preloadContextParties());
  }

  /** module + record the options currently belong to. */
  private contextPartyKey(): string {
    const moduleId = this.ticketForm?.get('module_id')?.value ?? '';
    const referenceId = this.ticketForm?.get('reference_id')?.value ?? '';
    return `${moduleId}:${referenceId}`;
  }

  private initializeEditModeEntityWatcher(): void {
    if (!this.ticketForm) {
      return;
    }

    // Watch for customer/vendor/subcontractor changes and preload entities for ng-select
    this.ticketForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(100),
        map(() => ({
          customer: this.ticketForm.get('customer')?.value,
          vendor: this.ticketForm.get('vendor')?.value,
          subcontractor: this.ticketForm.get('subcontractor')?.value,
        })),
        distinctUntilChanged(
          (prev, curr) =>
            prev.customer === curr.customer &&
            prev.vendor === curr.vendor &&
            prev.subcontractor === curr.subcontractor
        )
      )
      .subscribe(({ customer, vendor, subcontractor }) => {
        // GUARD: Only sync if values are IDs (numbers), not objects
        // This prevents infinite loops and re-processing after parent sets full object
        const shouldSync =
          (customer && typeof customer === 'number') ||
          (vendor && typeof vendor === 'number') ||
          (subcontractor && typeof subcontractor === 'number');

        if (shouldSync) {
          // Re-sync entities when form values change (called after populateFormFromModel in edit mode)
          this.syncFormToState();
        }
      });
  }

  /**
   * Initialize contextual preselection for customer/vendor/subcontractor
   * When ticket is created from a context (e.g., Job), automatically preselect
   * the associated entities if only one exists
   */
  private initializeContextualPreselection(
    moduleId: number,
    referenceId: number
  ): void {
    // Attempt to preselect customer if only one exists for this context
    const customerModel = {
      moduleId: moduleId, // Backend expects camelCase
      referenceId: referenceId, // Backend expects camelCase
      search: '', // Empty search to get all
    };

    this.ticketDataService
      .customers(customerModel)
      .pipe(
        map((res: any) => res?.data || []),
        catchError(() => of([]))
      )
      .subscribe((customers: any[]) => {
        // If exactly one customer exists, preselect it
        if (customers.length === 1) {
          const customer = customers[0];

          // Populate customerSearchResults$ with the preselected customer
          // so ng-select can display the customer name
          this.customerSearchResults$ = of([customer]);

          // Set form value
          this.ticketForm?.get('customer')?.setValue(customer.id);
          this.customerSelected.emit(customer);
          this.fieldChange.emit({ field: 'customer', value: customer.id });

          // Preselect contact based on context (Job/Customer/Vendor/Subcontractor)
          this.preselectContactByContext(moduleId, referenceId);

          this.cdr.markForCheck();
        }
      });

    // Note: Vendor and Subcontractor preselection can be added similarly if needed
    // For now, focusing on customer as that's the most common use case
  }

  /**
   * Preselect contact based on creation context
   * - Job: Preselect job's customer contact
   * - Customer: Preselect customer's primary contact
   * - Vendor: Preselect vendor's primary contact
   * - Subcontractor: Preselect subcontractor's primary contact
   */
  private preselectContactByContext(
    moduleId: number,
    referenceId: number
  ): void {
    // Find module details to determine type
    const selectedModule = this.modules.find((m) => m.id === moduleId);
    if (!selectedModule) return;

    const moduleSlug = (selectedModule.slug || '').toLowerCase();
    const moduleName = (selectedModule.name || '').toLowerCase();

    // Determine module type
    let moduleType: 'job' | 'customer' | 'vendor' | 'subcontractor' | null =
      null;

    if (moduleSlug === 'job' || moduleName.includes('job')) {
      moduleType = 'job';
    } else if (moduleSlug === 'customer' || moduleName.includes('customer')) {
      moduleType = 'customer';
    } else if (moduleSlug === 'vendor' || moduleName.includes('vendor')) {
      moduleType = 'vendor';
    } else if (
      moduleSlug === 'subcontractor' ||
      moduleName.includes('subcontractor')
    ) {
      moduleType = 'subcontractor';
    }

    if (!moduleType) return;

    // Fetch entity details and extract contact ID
    this.fetchEntityContactId(moduleType, referenceId)
      .pipe(
        catchError((error) => {
          console.warn(
            `Failed to fetch ${moduleType} details for contact preselection:`,
            error
          );
          return of(null);
        })
      )
      .subscribe((contactId: number | null) => {
        if (contactId) {
          // Wait for parent to populate contacts via getCustomerContacts()
          setTimeout(() => {
            this.ticketForm?.get('contact')?.setValue(contactId);
            this.contactSelected.emit(contactId);
            this.fieldChange.emit({ field: 'contact', value: contactId });
            this.cdr.markForCheck();
          }, 150); // Delay to ensure contacts are loaded
        }
      });
  }

  /**
   * Fetch entity details and extract contact ID based on module type
   */
  private fetchEntityContactId(
    moduleType: 'job' | 'customer' | 'vendor' | 'subcontractor',
    entityId: number
  ): Observable<number | null> {
    switch (moduleType) {
      case 'job':
        return this.jobDataService.detail(entityId).pipe(
          map((res: any) => {
            const jobData = res?.data;
            return (
              jobData?.customer_contact_id ||
              jobData?.contact_id ||
              jobData?.customer?.contact_id ||
              null
            );
          })
        );

      case 'customer':
        return this.customerDataService.detail(entityId).pipe(
          map((res: any) => {
            const customerData = res?.data;
            return (
              customerData?.primary_contact?.id ||
              customerData?.primary_contact_id ||
              customerData?.contact_id ||
              null
            );
          })
        );

      case 'vendor':
        return this.vendorDataService.detail(entityId).pipe(
          map((res: any) => {
            const vendorData = res?.data;
            return (
              vendorData?.primary_contact?.id ||
              vendorData?.primary_contact_id ||
              vendorData?.contact_id ||
              null
            );
          })
        );

      case 'subcontractor':
        return this.subcontractorDataService.detail(entityId).pipe(
          map((res: any) => {
            const subcontractorData = res?.data;
            return (
              subcontractorData?.primary_contact?.id ||
              subcontractorData?.primary_contact_id ||
              subcontractorData?.contact_id ||
              null
            );
          })
        );

      default:
        return of(null);
    }
  }

  /**
   * Fetch system tags from API
   * Fetches all system tags for the ticket module
   */
  private fetchSystemTags(): void {
    const moduleId = 8; // Ticket Module ID
    const isSystem = 1;

    this.api
      .getApi(`user/tags?module_id=${moduleId}&is_system=${isSystem}`)
      .pipe(
        map((response: any) => {
          // Process the API response to match TicketTag interface
          const dataArray = response?.data || response;
          if (Array.isArray(dataArray)) {
            return dataArray.map((tag: any) => ({
              id: tag.id,
              name: tag.tag || tag.name,
              tag: tag.tag || tag.name,
              color: tag.color || 'gray',
            }));
          }
          return [];
        }),
        catchError(() => {
          console.warn('Failed to fetch system tags for tickets');
          return of([]);
        })
      )
      .subscribe((tags: TicketTag[]) => {
        this.systemTags = tags;
        this.cdr.markForCheck();
      });
  }

  /**
   * Public method to refresh system tags from API
   * Called by parent component after tag creation to update dropdown options
   */
  public refreshSystemTags(): void {
    this.fetchSystemTags();
  }

  // =============================================================================
  // SEARCH METHODS
  // =============================================================================

  private searchPeopleByType(term: string, type: string): Observable<any[]> {
    // Based on type, search different endpoints
    switch (type) {
      case 'Users':
        return this.api
          .getApi(`staff/search?search=${term}`)
          .pipe(
            map((res: any) => this.staffService.processStaff(res?.data || []))
          );
      case 'Vendors':
        return this.api
          .postApi(`vendors/search?search=${term}`, '')
          .pipe(map((res: any) => res?.data || []));
      case 'Customers':
        return this.api
          .postApi(`customers/search?search=${term}`, '')
          .pipe(map((res: any) => res?.data || []));
      case 'Subcontractors':
        return this.api
          .postApi(`subcontractors/search?search=${term}`, '')
          .pipe(map((res: any) => res?.data || []));
      default:
        return of([]);
    }
  }

  /**
   * Fetches one page of the Assignee/Observer/Participant picker for the
   * given person type (empty `search` returns the default browsable list).
   * Users and Vendors read real pagination metadata; Customers and
   * Subcontractors have no confirmed pagination support on the backend, so
   * they fall back to `searchPeopleByType`'s flat single-shot search and
   * always report `hasMore: false`.
   */
  private fetchPeoplePage(
    type: string,
    search: string,
    page: number,
    fieldType?: 'assignee' | 'observer' | 'participant'
  ): Observable<{ items: any[]; currentPage: number; hasMore: boolean }> {
    if (type === 'Users') {
      return this.api
        .getApi('staff/search', { search, page, per_page: TicketSidebarComponent.PEOPLE_PER_PAGE })
        .pipe(
          map((response: any) => {
            const items = this.staffService.processStaff(response.data);
            const currentPage = response.meta?.current_page ?? page;
            const hasMore = currentPage < (response.meta?.last_page ?? currentPage);
            return { items, currentPage, hasMore };
          }),
          catchError(() => of({ items: [], currentPage: page, hasMore: false }))
        );
    }

    if (type === 'Vendors') {
      return this.api
        .postApi('vendors/search', { search, page, per_page: TicketSidebarComponent.PEOPLE_PER_PAGE })
        .pipe(
          map((response: any) => {
            // Paginated vendor responses come back double-wrapped as
            // { data: { data: [...], current_page, last_page } }.
            const envelope = response?.data?.data ? response.data : response;
            const items = envelope?.data ?? [];
            const currentPage = envelope?.current_page ?? page;
            const hasMore = currentPage < (envelope?.last_page ?? currentPage);
            return { items, currentPage, hasMore };
          }),
          catchError(() => of({ items: [], currentPage: page, hasMore: false }))
        );
    }

    return this.searchPeopleByType(search, type).pipe(
      map((items) => ({ items, currentPage: 1, hasMore: false })),
      catchError(() => of({ items: [], currentPage: 1, hasMore: false }))
    );
  }

  /**
   * Whether the star should show next to an already-selected Assignee/
   * Observer/Participant. Three cases: `isDefault` (camelCase) is set
   * transiently, in memory, the moment a person is picked/promoted during
   * THIS session; `is_default` (snake_case) is the real, persisted flag on
   * that ticket_associations row, returned by the API for an
   * already-saved ticket; and for a brand-new ticket, savedDefaults (from
   * GET ticket/default-people) is used as a fallback while pre-filling
   * empty fields. Only applies to 'Users' type people —
   * Vendors/Customers/Subcontractors aren't staff and can't be anyone's
   * saved default. `isDefault === false` is an explicit local override
   * (the user just promoted someone ELSE in this same list) — it must win
   * over the savedDefaults fallback, otherwise the old server-saved
   * default keeps matching by id and both people show starred at once.
   */
  isPersonDefault(person: any, personType: 'assignee' | 'observer' | 'participant'): boolean {
    if (!person) {
      return false;
    }
    if (person.isDefault === true || person.is_default === true) {
      return true;
    }
    if (person.isDefault === false) {
      return false;
    }
    // Wrapped { user, type } shape (live picker) is explicitly non-Users;
    // a flat shape (loaded from a saved task/ticket) has no `type` at all
    // and is assumed to be a Users record, since only staff can be a
    // saved default.
    if (person.type && person.type !== 'Users') {
      return false;
    }
    const savedId = this.savedDefaults?.[personType]?.id;
    const userData = person.user || person;
    return savedId != null && String(savedId) === String(userData?.id);
  }

  /**
   * Placeholder rows rendered in the picker while a page is in flight
   * (initial open, type-filter switch, typed search, or scroll-triggered
   * pagination).
   */
  private buildPeopleSkeletonRows(): any[] {
    return Array.from({ length: TicketSidebarComponent.SKELETON_ROW_COUNT }, (_, i) => ({
      __skeleton: true,
      id: `skeleton-${i}`,
    }));
  }

  private searchReferences(term: string, moduleId: number): Observable<any[]> {
    if (Number(moduleId) === this.selfAssignModuleId) {
      return of([]);
    }

    const selectedModule = this.modules.find(
      (m: any) => Number(m.id) === Number(moduleId)
    );

    if (!selectedModule || !selectedModule.slug) {
      return of([]);
    }

    const payload = {
      type: selectedModule.slug,
      module_id: selectedModule.id,
      search: term,
    };

    const normalizeReferenceResponse = (res: any): any[] => {
      const list = res?.data?.data || res?.data || [];
      return list.map((item: any) => ({
        id: item.id,
        name:
          item.data ||
          item.name ||
          item.reference_number ||
          item.number ||
          item.id,
      }));
    };

    return this.api.postApi('tasks/search-reference-numbers', payload).pipe(
      map((res: any) => normalizeReferenceResponse(res)),
      catchError(() =>
        // Fallback for environments still using the legacy template endpoint
        this.api.postApi('task-template/search-reference-numbers', payload).pipe(
          map((res: any) => normalizeReferenceResponse(res)),
          catchError(() => of([]))
        )
      )
    );
  }

  /**
   * Context-aware customer search
   * If moduleId and referenceId are provided, filters customers by context
   * Otherwise performs generic search
   */
  private searchCustomersContextual(
    term: string,
    moduleId?: number,
    referenceId?: number
  ): Observable<any[]> {
    // Skip contextual API call when self-assign is ON (module_id = 11)
    // Use generic search instead
    if (moduleId === this.selfAssignModuleId) {
      return this.api
        .postApi(`customers/search?search=${term}`, '')
        .pipe(map((res: any) => res?.data || []));
    }

    // If context is provided, use TicketDataService for filtered results
    if (moduleId && referenceId) {
      const model = {
        moduleId: moduleId, // Backend expects camelCase
        referenceId: referenceId, // Backend expects camelCase
        search: term,
      };
      return this.ticketDataService
        .customers(model)
        .pipe(map((res: any) => res?.data || []));
    }

    // Fallback to generic search if no context
    return this.api
      .postApi(`customers/search?search=${term}`, '')
      .pipe(map((res: any) => res?.data || []));
  }

  /**
   * Context-aware vendor search
   * If moduleId and referenceId are provided, filters vendors by context
   * Otherwise performs generic search
   */
  private searchVendorsContextual(
    term: string,
    moduleId?: number,
    referenceId?: number
  ): Observable<any[]> {
    // Skip contextual API call when self-assign is ON (module_id = 11)
    // Use generic search instead
    if (moduleId === this.selfAssignModuleId) {
      return this.api
        .postApi(`vendors/search?search=${term}`, '')
        .pipe(map((res: any) => res?.data || []));
    }

    // Job tickets use the scoped endpoint: only parties named on the job's
    // APPROVED line items, which the older endpoint does not filter for.
    if (moduleId && referenceId && this.isJobModule(moduleId)) {
      return this.ticketDataService
        .scopedVendors({
          module_id: moduleId,
          reference_id: referenceId,
          search: term,
        })
        .pipe(map((res: any) => res?.data || []));
    }

    // Every other module keeps the original ticket/vendors endpoint, exactly
    // as before — the scoped one only knows how to narrow by Job.
    if (moduleId && referenceId) {
      return this.ticketDataService
        .vendors({
          moduleId: moduleId, // Backend expects camelCase
          referenceId: referenceId,
          search: term,
        })
        .pipe(map((res: any) => res?.data || []));
    }

    // Fallback to generic search if no context
    return this.api
      .postApi(`vendors/search?search=${term}`, '')
      .pipe(map((res: any) => res?.data || []));
  }

  /**
   * Context-aware subcontractor search
   * If moduleId and referenceId are provided, filters subcontractors by context
   * Otherwise performs generic search
   */
  private searchSubcontractorsContextual(
    term: string,
    moduleId?: number,
    referenceId?: number
  ): Observable<any[]> {
    // Skip contextual API call when self-assign is ON (module_id = 11)
    // Use generic search instead
    if (moduleId === this.selfAssignModuleId) {
      return this.api
        .postApi(`subcontractors/search?search=${term}`, '')
        .pipe(map((res: any) => res?.data || []));
    }

    // Job tickets use the scoped endpoint: only parties named on the job's
    // APPROVED line items, which the older endpoint does not filter for.
    if (moduleId && referenceId && this.isJobModule(moduleId)) {
      return this.ticketDataService
        .scopedSubcontractors({
          module_id: moduleId,
          reference_id: referenceId,
          search: term,
        })
        .pipe(map((res: any) => res?.data || []));
    }

    // Every other module keeps the original ticket/subcontractors endpoint, exactly
    // as before — the scoped one only knows how to narrow by Job.
    if (moduleId && referenceId) {
      return this.ticketDataService
        .subcontractors({
          moduleId: moduleId, // Backend expects camelCase
          referenceId: referenceId,
          search: term,
        })
        .pipe(map((res: any) => res?.data || []));
    }

    // Fallback to generic search if no context
    return this.api
      .postApi(`subcontractors/search?search=${term}`, '')
      .pipe(map((res: any) => res?.data || []));
  }

  // =============================================================================
  // ANIMATION STATE METHODS
  // =============================================================================

  public getSidebarAnimationState(): string {
    return this.isCollapsed ? 'collapsed' : 'expanded';
  }

  public getToggleButtonAnimationState(): string {
    return this.isToggleButtonHovered ? 'hovered' : 'normal';
  }

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  public expandSidebar(): void {
    this.isCollapsed = false;
    this.onCollapse.emit(false);
    this.cdr.markForCheck();
  }

  public collapseSidebar(): void {
    this.isCollapsed = true;
    this.onCollapse.emit(true);
    this.cdr.markForCheck();
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery = value;
    this.onSearch.emit(value);
  }

  onStatusChange(event: any): void {
    // Extract value from event object (or use directly if value is passed)
    const statusId = event?.value || event;

    // Find the full status object by ID
    const selectedStatusObj = this.statuses.find((s: any) => s.id === statusId);

    if (selectedStatusObj) {
      // Emit to parent - parent handles form control update
      this.selectedStatus = selectedStatusObj;
      this.statusChange.emit(selectedStatusObj);
      this.cdr.markForCheck();
    }
  }

  onPriorityChange(priorityId: number): void {
    // Find the full priority object by ID
    const selectedPriorityObj = this.priorities.find(
      (p: any) => p.id === priorityId
    );

    if (selectedPriorityObj) {
      this.priorityChange.emit(selectedPriorityObj);
      this.fieldChange.emit({ field: 'priority', value: priorityId });
      this.cdr.markForCheck();
    }
  }

  // Milestone methods
  toggleMilestoneSelector(): void {
    this.showMilestoneSelector = !this.showMilestoneSelector;
  }

  selectMilestone(milestone: TicketMilestone): void {
    this.selectedMilestone = milestone;
    this.showMilestoneSelector = false;
    this.milestoneSelected.emit(milestone);
    this.fieldChange.emit({ field: 'milestone_id', value: milestone.id });
  }

  removeMilestone(): void {
    this.selectedMilestone = null;
    this.milestoneRemoved.emit();
    this.fieldChange.emit({ field: 'milestone_id', value: null });
  }

  // People methods
  onAssigneeSelect(person: any): void {
    if (!person || person.__skeleton) {
      return;
    }
    const ticketPerson = {
      user: person, // Nest person inside 'user' property for parent compatibility
      type: this.activeAssigneePersonType,
    };
    this.assigneeSelected.emit(ticketPerson);
    this.showAssigneeSearch = false;
    // Reset loading state after selection
    this.assigneeSearchLoading = false;
    this.cdr.markForCheck();
  }

  onObserverSelect(person: any): void {
    if (!person || person.__skeleton) {
      return;
    }
    const ticketPerson = {
      user: person, // Nest person inside 'user' property for parent compatibility
      type: this.activeObserverPersonType,
    };
    this.observerSelected.emit(ticketPerson);
    this.showObserverSearch = false;
    // Reset loading state after selection
    this.observerSearchLoading = false;
    this.cdr.markForCheck();
  }

  onParticipantSelect(person: any): void {
    if (!person || person.__skeleton) {
      return;
    }
    const ticketPerson = {
      user: person, // Nest person inside 'user' property for parent compatibility
      type: this.activeParticipantPersonType,
    };
    this.participantSelected.emit(ticketPerson);
    this.showParticipantSearch = false;
    // Reset loading state after selection
    this.participantSearchLoading = false;
    this.cdr.markForCheck();
  }

  /**
   * Star next to an ALREADY-selected assignee (in the People list itself,
   * not the dropdown) — promotes them to default. `person` here already
   * arrives as {user, type} (the shape addUser() stores), so it's emitted
   * as-is; the parent handler knows how to remap the flag in place since
   * this person is already in the list.
   */
  setSelectedAssigneeAsDefault(person: any, event: Event): void {
    event.stopPropagation();
    this.defaultAssigneeChanged.emit({ ...person, isDefault: true });
  }

  /** Same as setSelectedAssigneeAsDefault, for the Observer list. */
  setSelectedObserverAsDefault(person: any, event: Event): void {
    event.stopPropagation();
    this.defaultObserverChanged.emit({ ...person, isDefault: true });
  }

  /** Same as setSelectedAssigneeAsDefault, for the Participant list. */
  setSelectedParticipantAsDefault(person: any, event: Event): void {
    event.stopPropagation();
    this.defaultParticipantChanged.emit({ ...person, isDefault: true });
  }

  removeAssignee(index: number): void {
    this.assigneeRemoved.emit(index);
  }

  removeObserver(index: number): void {
    this.observerRemoved.emit(index);
  }

  removeParticipant(index: number): void {
    this.participantRemoved.emit(index);
  }

  // =============================================================================
  // TAG METHODS
  // =============================================================================

  /**
   * Open tag selector popup for creating new tags
   * Emits event to parent component to handle popup
   */
  public openTagSelector(): void {
    this.tagSelectorOpened.emit();
  }

  /**
   * Remove tag by clicking X button on chip
   * Works in all modes - triggers immediate sync to parent
   */
  public removeTagFromDisplayed(index: number): void {
    const currentTags = this.tags || [];
    if (index < 0 || index >= currentTags.length) return;

    const updatedTags = [
      ...currentTags.slice(0, index),
      ...currentTags.slice(index + 1),
    ];

    // Update both tags input and dropdown selection
    this.selectedTagsForDropdown = [...updatedTags];

    // Sync to parent immediately
    this.tagsUpdated.emit(updatedTags);
  }

  /**
   * Open tags dropdown - Works for all modes (Create, Edit, View)
   */
  public openTagsDropdown(): void {
    // Sync selected tags from input tags
    // Map current tags to match combined tags structure
    this.selectedTagsForDropdown = (this.tags || []).map((tag) => {
      // Find matching tag in combined list or use the tag as-is
      const matchingTag = this.combinedTagsForDropdown.find(
        (ct) =>
          (ct.name || ct.tag || '').toLowerCase() ===
          (tag.name || tag.tag || '').toLowerCase()
      );
      return matchingTag || { ...tag };
    });
    this.tagSearchQuery = '';
    this.isTagsDropdownOpen = true;

    // Trigger change detection for OnPush strategy
    this.cdr.markForCheck();

    // Focus ng-select after a brief delay to ensure it's rendered
    setTimeout(() => {
      if (this.tagsNgSelect) {
        this.tagsNgSelect.focus();
      }
    }, 100);
  }

  /**
   * Close tags dropdown - Works for all modes
   */
  public closeTagsDropdown(): void {
    // Close ng-select if it's open
    if (this.tagsNgSelect && this.tagsNgSelect.isOpen) {
      this.tagsNgSelect.close();
    }
    this.isTagsDropdownOpen = false;
    this.tagSearchQuery = '';
    // Sync selected tags back to parent
    this.syncTagsToParent();
  }

  /**
   * Toggle tag selection in dropdown - Works for all modes
   * @param tag Tag to toggle
   */
  public toggleTagSelection(tag: TicketTag): void {
    const index = this.selectedTagsForDropdown.findIndex(
      (selected) =>
        (selected.name || selected.tag || '').toLowerCase() ===
        (tag.name || tag.tag || '').toLowerCase()
    );

    if (index > -1) {
      // Remove tag
      this.selectedTagsForDropdown = [
        ...this.selectedTagsForDropdown.slice(0, index),
        ...this.selectedTagsForDropdown.slice(index + 1),
      ];
    } else {
      // Add tag
      this.selectedTagsForDropdown = [
        ...this.selectedTagsForDropdown,
        { ...tag },
      ];
    }

    // Sync immediately for all modes
    this.syncTagsToParent();
  }

  /**
   * Handle tag selection from ng-select - Works for all modes
   * @param selectedTags Array of selected tags from ng-select
   */
  public onTagsSelectChange(selectedTags: TicketTag[]): void {
    this.selectedTagsForDropdown = selectedTags || [];
    this.syncTagsToParent();
  }

  /**
   * Sync selected tags to parent component - Works for all modes
   * In View mode, parent will make immediate API calls
   * In Create/Edit mode, parent will update form control
   */
  private syncTagsToParent(): void {
    const normalizedTags: TicketTag[] = (
      this.selectedTagsForDropdown || []
    ).map((tag) => ({
      id: tag.id,
      name: tag.name || tag.tag || '',
      tag: tag.tag || tag.name || '',
      color: tag.color || 'gray',
    }));

    this.tagsUpdated.emit(normalizedTags);
  }

  /**
   * Compare function for ng-select to match tags by name
   * Arrow function to preserve 'this' context in template
   */
  public compareTags = (tag1: TicketTag, tag2: TicketTag): boolean => {
    if (!tag1 || !tag2) return false;
    return (
      (tag1.name || tag1.tag || '').toLowerCase() ===
      (tag2.name || tag2.tag || '').toLowerCase()
    );
  };

  /**
   * Track by function for ng-select to properly identify items
   */
  public trackByTagName = (index: number, item: TicketTag): string => {
    return (item?.name || item?.tag || index.toString()).toLowerCase();
  };

  // Time Planning methods
  toggleTimePlanning(): void {
    this.isTimePlanningExpanded = !this.isTimePlanningExpanded;
    this.ticketForm.patchValue({
      is_time_planning: this.isTimePlanningExpanded,
    });
    this.timePlanningToggle.emit(this.isTimePlanningExpanded);
  }

  /**
   * Handle start date change event from input
   */
  onStartDateChange(event: any): void {
    const startDate = event?.target?.value;
    if (startDate) {
      // Update form control directly for immediate UI update
      this.ticketForm
        ?.get('start_date')
        ?.setValue(startDate, { emitEvent: false });
      // Emit Date object to parent for additional processing
      this.startDateChange.emit(new Date(startDate));
      this.validateTimePlanning();
      this.calculateDurationFromDates();
    }
  }

  /**
   * Handle duration change event (auto-update finish date)
   */
  onDurationChange(event: any): void {
    const duration = parseFloat(event?.target?.value);
    if (!isNaN(duration) && duration >= 0) {
      this.durationChange.emit(duration);
      this.ticketForm
        ?.get('duration')
        ?.setValue(duration, { emitEvent: false });
      this.calculateFinishDate();
    }
  }

  /**
   * Handle finish date change event
   */
  onFinishDateChange(event: any): void {
    const finishDate = event?.target?.value;
    if (finishDate) {
      // Update form control directly for immediate UI update
      this.ticketForm
        ?.get('date_finished')
        ?.setValue(finishDate, { emitEvent: false });
      // Emit Date object to parent for additional processing
      this.finishDateChange.emit(new Date(finishDate));
      this.validateTimePlanning();
      this.calculateDurationFromDates();
    }
  }

  /**
   * Validate time planning dates (start must be before finish)
   */
  validateTimePlanning(): void {
    const startDateValue = this.ticketForm?.get('start_date')?.value;
    const finishDateValue = this.ticketForm?.get('date_finished')?.value;

    if (startDateValue && finishDateValue) {
      const start = new Date(startDateValue);
      const finish = new Date(finishDateValue);
      start.setHours(0, 0, 0, 0);
      finish.setHours(0, 0, 0, 0);

      if (start > finish) {
        this.timePlanningError = 'Start date must be before finish date';
      } else {
        this.timePlanningError = '';
      }
    } else {
      this.timePlanningError = '';
    }
  }

  /**
   * Calculate finish date based on start date + duration
   */
  private calculateFinishDate(): void {
    const startDateValue = this.ticketForm?.get('start_date')?.value;
    const duration = this.ticketForm?.get('duration')?.value;

    if (startDateValue && duration && duration > 0) {
      const start = new Date(startDateValue);
      const finish = new Date(start);
      finish.setDate(finish.getDate() + duration);

      const finishFormatted = finish.toISOString().split('T')[0];
      this.ticketForm
        ?.get('date_finished')
        ?.setValue(finishFormatted, { emitEvent: false });
      this.validateTimePlanning();
    }
  }

  /**
   * Calculate duration based on start/finish
   */
  private calculateDurationFromDates(): void {
    const startDateValue = this.ticketForm?.get('start_date')?.value;
    const finishDateValue = this.ticketForm?.get('date_finished')?.value;

    if (startDateValue && finishDateValue) {
      const start = new Date(startDateValue);
      const finish = new Date(finishDateValue);
      start.setHours(0, 0, 0, 0);
      finish.setHours(0, 0, 0, 0);

      if (start <= finish) {
        const diffTime = finish.getTime() - start.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        this.ticketForm
          ?.get('duration')
          ?.setValue(diffDays, { emitEvent: false });
      }
    }
  }

  /**
   * Determine if a checklist item is completed
   */
  private isChecklistItemCompleted(item: any): boolean {
    const value =
      item?.is_completed ?? item?.completed ?? item?.status ?? false;

    if (typeof value === 'string') {
      return value === '1' || value.toLowerCase() === 'true';
    }

    return value === true || value === 1;
  }

  // Module & Reference methods
  onModuleChange(moduleId: any): void {
    // Find the full module object by ID
    const selectedModuleObj = this.modules.find((m: any) => m.id == moduleId);

    if (selectedModuleObj) {
      this.moduleSelected.emit(selectedModuleObj);
      this.fieldChange.emit({
        field: 'module_id',
        value: selectedModuleObj.id,
      });

      // Changing module clears the reference, so any preloaded parties belong to
      // a record that is no longer selected.
      this.vendorOptions$.next([]);
      this.subcontractorOptions$.next([]);
      this.cdr.markForCheck();
    }
  }

  onReferenceSelect(reference: any): void {
    this.referenceSelected.emit(reference);
    this.fieldChange.emit({ field: 'reference_id', value: reference?.id });

    // The parent writes reference_id back into the form, so wait a tick before
    // reading it — otherwise the preload would run against the previous record.
    setTimeout(() => this.preloadContextParties(), 0);
  }

  /** Handle self-assign toggle change */
  onSelfAssignToggle(event: any): void {
    const isOn = !!(event && (event.checked ?? event.target?.checked));
    this.fieldChange.emit({ field: 'self_assign', value: isOn ? 1 : 0 });
    this.selfAssignChange.emit(isOn);

    if (isOn) {
      const loginUserId = this.userService?.getID?.();
      const referenceValue = loginUserId ? Number(loginUserId) : null;

      this.ticketForm?.get('module_id')?.setValue(this.selfAssignModuleId);
      this.ticketForm?.get('reference_id')?.setValue(referenceValue);

      this.fieldChange.emit({
        field: 'module_id',
        value: this.selfAssignModuleId,
      });
      this.fieldChange.emit({ field: 'reference_id', value: referenceValue });
    } else {
      this.ticketForm?.get('module_id')?.setValue(null);
      this.ticketForm?.get('reference_id')?.setValue(null);
      this.fieldChange.emit({ field: 'module_id', value: null });
      this.fieldChange.emit({ field: 'reference_id', value: null });
    }
    this.cdr.markForCheck();
  }

  // Ticket-specific methods
  onTicketTypeChange(ticketType: any): void {
    // Convert to number and find full type object
    const typeId = Number(ticketType);
    const selectedTypeObj = this.ticketTypes.find((t: any) => t.id === typeId);

    // Emit full object for parent workflows
    if (selectedTypeObj) {
      this.ticketTypeSelected.emit(selectedTypeObj);
    }

    // Emit numeric ID for form control update
    this.fieldChange.emit({ field: 'ticket_type_id', value: typeId });
  }

  onScopesChange(scopes: any[]): void {
    // Ensure we emit array of IDs only (defensive check for ng-select output)
    // ng-select with bindValue="id" should emit IDs, but normalize to ensure consistency
    const scopeIds = Array.isArray(scopes)
      ? scopes.map((s) => (typeof s === 'object' && s !== null ? s.id : s))
      : [];

    this.scopesChanged.emit(scopeIds);
    this.fieldChange.emit({ field: 'scopes', value: scopeIds });
  }

  onCustomerChange(customer: any): void {
    this.customerSelected.emit(customer);
    this.fieldChange.emit({ field: 'customer', value: customer?.id });
  }

  onContactChange(contact: any): void {
    // Convert string from native select to number (same pattern as ticket type)
    // Native select returns string, but form control expects number
    const contactId = Number(contact);

    this.contactSelected.emit(contactId);
    this.fieldChange.emit({ field: 'contact', value: contactId });
  }

  onVendorChange(vendor: any): void {
    this.vendorSelected.emit(vendor);
    this.fieldChange.emit({ field: 'vendor', value: vendor?.id });
    this.resetTypeaheadTerm(this.vendorSearchInput$);
  }

  onSubcontractorChange(subcontractor: any): void {
    this.subcontractorSelected.emit(subcontractor);
    this.fieldChange.emit({ field: 'subcontractor', value: subcontractor?.id });
    this.resetTypeaheadTerm(this.subcontractorSearchInput$);
  }

  /**
   * Forget the last search term after a selection or a clear.
   *
   * The typeahead pipes through distinctUntilChanged(), which remembers the last
   * term it saw. Search "abc", pick a result, clear it, then type "abc" again and
   * the identical term is swallowed — no request, no results. Emitting a blank
   * term resets that memory (and is ignored by the <2 character guard).
   */
  private resetTypeaheadTerm(input$: Subject<string>): void {
    input$.next('');
  }

  onResponsiblePartyChange(partyId: any): void {
    const selectedParty = this.responsibleParties.find((p) => p.id == partyId);
    this.responsiblePartySelected.emit(selectedParty);
    this.fieldChange.emit({ field: 'party_id', value: partyId });
  }

  loadResponsibleParties(): void {
    this.settingDataService
      .getTaskPartyListing(false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // Handle both wrapped ({data: [...]}) and unwrapped ([...]) responses
          this.responsibleParties = response?.data || response || [];
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading responsible parties:', error);
          this.responsibleParties = [];
          this.cdr.markForCheck();
        },
      });
  }

  // =============================================================================
  // HELPER METHODS
  // =============================================================================

  getPersonDisplayName(person: any): string {
    // Support both nested { user: {...}, type } and flat { id, name, ... } structures
    const userData = person.user || person;
    return userData.full_name || userData.name || userData.company_name || '';
  }

  getPersonTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      Users: 'person',
      Vendors: 'business',
      Customers: 'account_circle',
      Subcontractors: 'engineering',
    };
    return icons[type] || 'person';
  }

  getMilestoneStatusBadgeClass(status: string): string {
    const classes: Record<string, string> = {
      upcoming: 'badge-upcoming',
      'in-progress': 'badge-in-progress',
      completed: 'badge-completed',
      overdue: 'badge-overdue',
    };
    return classes[status] || '';
  }

  /**
   * Get selected ticket type name for display in view mode
   * @returns Ticket type name or empty string if not found
   */
  getSelectedTicketTypeName(): string {
    const ticketTypeId = this.ticketForm?.get('ticket_type_id')?.value;
    if (!ticketTypeId || !this.ticketTypes) return '';

    const ticketType = this.ticketTypes.find((t: any) => t.id === ticketTypeId);
    return ticketType?.name || '';
  }

  /**
   * Get selected scopes display string for view mode
   * Returns comma-separated list of scope names
   * @returns Comma-separated scope names or empty string
   */
  getSelectedScopesDisplay(): string {
    const scopeIds = this.ticketForm?.get('scopes')?.value;
    if (
      !scopeIds ||
      !Array.isArray(scopeIds) ||
      scopeIds.length === 0 ||
      !this.scopes
    ) {
      return '';
    }

    const selectedScopes = scopeIds
      .map((id: number) => {
        const scope = this.scopes.find((s: any) => s.id === id);
        return scope?.name;
      })
      .filter((name: string | undefined) => name); // Remove undefined values

    return selectedScopes.join(', ');
  }

  getPriorityColor(priorityId: number): string {
    const priority = this.priorities.find((p) => p.id === priorityId);
    return priority?.color || '#6b7280';
  }

  getStatusColor(status: any): string {
    return status?.color || '#6b7280';
  }

  /**
   * Get selected status name for display in trigger
   * @returns Status name or empty string if not found
   */
  public getSelectedStatusName(): string {
    const statusId = this.ticketForm?.get('status')?.value;
    if (!statusId || !this.statuses) return '';

    const status = this.statuses.find((s) => s.id === statusId);
    return status?.name || '';
  }

  /**
   * Get selected status color for display in trigger
   * @returns Status color (hex code) or default gray if not found
   */
  public getSelectedStatusColor(): string {
    const statusId = this.ticketForm?.get('status')?.value;
    if (!statusId || !this.statuses) return '#9ca3af'; // Default gray

    const status = this.statuses.find((s) => s.id === statusId);
    return status?.color || '#9ca3af';
  }

  /**
   * Get selected status lock state for display in trigger
   * @returns True if selected status has lock_status enabled
   */
  public getSelectedStatusLocked(): boolean {
    const statusId = this.ticketForm?.get('status')?.value;
    if (!statusId || !this.statuses) return false;

    const status = this.statuses.find((s) => s.id === statusId);
    return status?.lock_status || false;
  }

  getPersonAvatar(person: any): string | null {
    // Support both nested { user: {...}, type } and flat { id, name, ... } structures
    const userData = person?.user || person;
    if (!userData) {
      return null;
    }

    const profileImage = userData.profile_image;
    const profileImageUrl =
      typeof profileImage === 'string' ? profileImage : profileImage?.url;

    return (
      userData.avatar ||
      userData.avatar_url ||
      userData.photo ||
      userData.image_url ||
      userData.profile_url ||
      profileImageUrl ||
      null
    );
  }

  // =============================================================================
  // PERSON TYPE MANAGEMENT
  // =============================================================================

  setActiveAssigneePersonType(
    type: 'Users' | 'Vendors' | 'Customers' | 'Subcontractors'
  ): void {
    this.activeAssigneePersonType = type;
    this.loadAssigneeBrowseList();
    // The type-filter button sits outside the ng-select host, so clicking it
    // registers as an outside click and ng-select auto-closes its panel —
    // reopen it, same as the initial "+" click does.
    setTimeout(() => this.assigneeSelectRef?.open());
  }

  setActiveObserverPersonType(
    type: 'Users' | 'Vendors' | 'Customers' | 'Subcontractors'
  ): void {
    this.activeObserverPersonType = type;
    this.loadObserverBrowseList();
    setTimeout(() => this.observerSelectRef?.open());
  }

  setActiveParticipantPersonType(
    type: 'Users' | 'Vendors' | 'Customers' | 'Subcontractors'
  ): void {
    this.activeParticipantPersonType = type;
    this.loadParticipantBrowseList();
    setTimeout(() => this.participantSelectRef?.open());
  }

  openAssigneeSelector(): void {
    this.showAssigneeSearch = !this.showAssigneeSearch;
    if (this.showAssigneeSearch) {
      this.loadAssigneeBrowseList();
      // ng-select isn't in the DOM until this change detection pass finishes,
      // so ViewChild only resolves on the next tick.
      setTimeout(() => this.assigneeSelectRef?.open());
    } else {
      this.assigneeSearchInput$.next('');
    }
    this.cdr.markForCheck();
  }

  openObserverSelector(): void {
    this.showObserverSearch = !this.showObserverSearch;
    if (this.showObserverSearch) {
      this.loadObserverBrowseList();
      setTimeout(() => this.observerSelectRef?.open());
    } else {
      this.observerSearchInput$.next('');
    }
    this.cdr.markForCheck();
  }

  openParticipantSelector(): void {
    this.showParticipantSearch = !this.showParticipantSearch;
    if (this.showParticipantSearch) {
      this.loadParticipantBrowseList();
      setTimeout(() => this.participantSelectRef?.open());
    } else {
      this.participantSearchInput$.next('');
    }
    this.cdr.markForCheck();
  }

  /**
   * Populate the assignee picker with the default browsable list (page 1,
   * empty search term, currently active person type) as soon as it opens or
   * its type filter changes, instead of leaving it empty until the user
   * types.
   */
  private loadAssigneeBrowseList(): void {
    this.assigneeSearchTerm = '';
    this.assigneeSearchLoading = true;
    this.assigneeItems = this.buildPeopleSkeletonRows();
    this.cdr.markForCheck();
    this.fetchPeoplePage(this.activeAssigneePersonType, '', 1, 'assignee').subscribe((result) => {
      this.assigneeItems = result.items;
      this.assigneeCurrentPage = result.currentPage;
      this.assigneeHasMore = result.hasMore;
      this.assigneeSearchLoading = false;
      this.cdr.markForCheck();
    });
  }

  private loadObserverBrowseList(): void {
    this.observerSearchTerm = '';
    this.observerSearchLoading = true;
    this.observerItems = this.buildPeopleSkeletonRows();
    this.cdr.markForCheck();
    this.fetchPeoplePage(this.activeObserverPersonType, '', 1, 'observer').subscribe((result) => {
      this.observerItems = result.items;
      this.observerCurrentPage = result.currentPage;
      this.observerHasMore = result.hasMore;
      this.observerSearchLoading = false;
      this.cdr.markForCheck();
    });
  }

  private loadParticipantBrowseList(): void {
    this.participantSearchTerm = '';
    this.participantSearchLoading = true;
    this.participantItems = this.buildPeopleSkeletonRows();
    this.cdr.markForCheck();
    this.fetchPeoplePage(this.activeParticipantPersonType, '', 1, 'participant').subscribe((result) => {
      this.participantItems = result.items;
      this.participantCurrentPage = result.currentPage;
      this.participantHasMore = result.hasMore;
      this.participantSearchLoading = false;
      this.cdr.markForCheck();
    });
  }

  /** Fetch the next page when the assignee dropdown is scrolled to the end. */
  onAssigneeScrollToEnd(): void {
    if (!this.PAGINATED_PERSON_TYPES.includes(this.activeAssigneePersonType)) {
      return;
    }
    if (this.assigneeSearchLoading || this.assigneeLoadingMore || !this.assigneeHasMore) {
      return;
    }
    this.assigneeLoadingMore = true;
    const loadedItems = this.assigneeItems;
    this.assigneeItems = [...loadedItems, ...this.buildPeopleSkeletonRows()];
    this.cdr.markForCheck();
    this.fetchPeoplePage(
      this.activeAssigneePersonType,
      this.assigneeSearchTerm,
      this.assigneeCurrentPage + 1,
      'assignee'
    ).subscribe((result) => {
      this.assigneeItems = [...loadedItems, ...result.items];
      this.assigneeCurrentPage = result.currentPage;
      this.assigneeHasMore = result.hasMore;
      this.assigneeLoadingMore = false;
      this.cdr.markForCheck();
    });
  }

  /** Fetch the next page when the observer dropdown is scrolled to the end. */
  onObserverScrollToEnd(): void {
    if (!this.PAGINATED_PERSON_TYPES.includes(this.activeObserverPersonType)) {
      return;
    }
    if (this.observerSearchLoading || this.observerLoadingMore || !this.observerHasMore) {
      return;
    }
    this.observerLoadingMore = true;
    const loadedItems = this.observerItems;
    this.observerItems = [...loadedItems, ...this.buildPeopleSkeletonRows()];
    this.cdr.markForCheck();
    this.fetchPeoplePage(
      this.activeObserverPersonType,
      this.observerSearchTerm,
      this.observerCurrentPage + 1,
      'observer'
    ).subscribe((result) => {
      this.observerItems = [...loadedItems, ...result.items];
      this.observerCurrentPage = result.currentPage;
      this.observerHasMore = result.hasMore;
      this.observerLoadingMore = false;
      this.cdr.markForCheck();
    });
  }

  /** Fetch the next page when the participant dropdown is scrolled to the end. */
  onParticipantScrollToEnd(): void {
    if (!this.PAGINATED_PERSON_TYPES.includes(this.activeParticipantPersonType)) {
      return;
    }
    if (this.participantSearchLoading || this.participantLoadingMore || !this.participantHasMore) {
      return;
    }
    this.participantLoadingMore = true;
    const loadedItems = this.participantItems;
    this.participantItems = [...loadedItems, ...this.buildPeopleSkeletonRows()];
    this.cdr.markForCheck();
    this.fetchPeoplePage(
      this.activeParticipantPersonType,
      this.participantSearchTerm,
      this.participantCurrentPage + 1,
      'participant'
    ).subscribe((result) => {
      this.participantItems = [...loadedItems, ...result.items];
      this.participantCurrentPage = result.currentPage;
      this.participantHasMore = result.hasMore;
      this.participantLoadingMore = false;
      this.cdr.markForCheck();
    });
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Get tag background color
   * @param color Color key or hex color
   * @returns RGBA color string
   */
  public getTagBackgroundColor(color: string): string {
    if (!color) {
      return this.TAG_COLORS.gray.bg;
    }

    // If color is a HEX (e.g. #22c55e), use a translucent background from it
    if (this.isHexColor(color)) {
      return this.hexToRgba(color, 0.2);
    }

    return this.TAG_COLORS[color]?.bg || this.TAG_COLORS.gray.bg;
  }

  /**
   * Get tag text color
   * @param color Color key or hex color
   * @returns Hex color string
   */
  public getTagTextColor(color: string): string {
    if (!color) {
      return this.TAG_COLORS.gray.text;
    }

    // If color is a HEX, use it directly for the text color
    if (this.isHexColor(color)) {
      return color;
    }

    return this.TAG_COLORS[color]?.text || this.TAG_COLORS.gray.text;
  }

  /** Determine if a string is a HEX color like #fff or #ffffff */
  private isHexColor(value: string): boolean {
    return /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test((value || '').trim());
  }

  /** Convert HEX color to rgba string with provided alpha */
  private hexToRgba(hex: string, alpha: number): string {
    const clean = hex.replace('#', '');
    const expand =
      clean.length === 3
        ? clean
            .split('')
            .map((c) => c + c)
            .join('')
        : clean;
    const r = parseInt(expand.substring(0, 2), 16);
    const g = parseInt(expand.substring(2, 4), 16);
    const b = parseInt(expand.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // =============================================================================
  // PROGRESS BAR UTILITIES
  // =============================================================================

  /**
   * Get progress bar color based on completion percentage and ticket status
   * @returns Bootstrap color class (bg-success, bg-danger, bg-warning, bg-primary)
   */
  public getProgressColor(): string {
    const status = this.getTicketStatus();
    return this.ticketUtilsService.getProgressColor(
      this.completionPercentage,
      status
    );
  }

  /**
   * Get current ticket status from form or selected status
   * @returns Status string
   */
  public getTicketStatus(): string {
    if (this.selectedStatus) {
      return (
        this.selectedStatus.name ||
        this.selectedStatus.label ||
        this.selectedStatus.status_name ||
        ''
      );
    }

    if (this.ticketForm) {
      const statusValue =
        this.ticketForm.get('status_id')?.value ||
        this.ticketForm.get('status')?.value;
      if (statusValue) {
        // If it's an object with name/label
        if (typeof statusValue === 'object') {
          return (
            statusValue.name ||
            statusValue.label ||
            statusValue.status_name ||
            ''
          );
        }
        // If it's a string
        return String(statusValue);
      }
    }

    return '';
  }

  /**
   * Get completed checklist items count
   * Calculates from checklist array @Input to ensure OnPush change detection works
   * @returns Number of completed items
   */
  public get completedCount(): number {
    if (!this.checklist || !Array.isArray(this.checklist)) {
      return 0;
    }
    return this.checklist.filter((item: any) =>
      this.isChecklistItemCompleted(item)
    ).length;
  }

  /**
   * Get total checklist items count
   * Calculates from checklist array @Input to ensure OnPush change detection works
   * @returns Total number of checklist items
   */
  public get totalCount(): number {
    if (!this.checklist || !Array.isArray(this.checklist)) {
      return 0;
    }
    return this.checklist.length;
  }

  /**
   * Check if we're editing an existing ticket (vs creating new)
   * Matches task-sidebar pattern for consistency
   * @returns true if editing existing ticket, false if creating new
   */
  public isEditingExistingTicket(): boolean {
    return (
      typeof this.ticketId === 'number' ||
      (typeof this.ticketId === 'string' &&
        this.ticketId !== 'New' &&
        !isNaN(Number(this.ticketId)))
    );
  }

  // =============================================================================
  // LIFECYCLE CLEANUP
  // =============================================================================

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
